import { useCallback, useState } from 'react';

import useCollectionConfigWrite from './useCollectionConfigWrite';

/**
 * Owns inline per-object color: the in-progress preview and the single write
 * that lands when the picker closes.
 *
 * The pointer commits on pick; color cannot — a hue drag would fire a PATCH per
 * frame. So the two halves are split: `previewColor` moves the map only, and
 * `commitColor` is the one write.
 *
 * `liveStyling` keeps the exact shape the map already consumed while the object
 * panel owned color (`{ objectKey, color, opacity }`), so `resolveObjects` and
 * both map layers are untouched by the move.
 *
 * The preview is held until the save settles rather than cleared when it starts:
 * the optimistic cache write happens a tick later, inside `onMutate`, so
 * dropping the override first would show the old color for a frame. It carries
 * the committed value, so nothing changes on screen when it goes.
 *
 * @param {string} collectionUuid
 * @param {Array}  objects - enriched collection objects from useCollectionData
 */
const useObjectStyle = (collectionUuid, objects) => {
	const [liveStyling, setLiveStyling] = useState(null);

	const { save, isSaving } = useCollectionConfigWrite(collectionUuid, objects, {
		id: 'object-color-error',
		title: 'Failed to save object color',
	});

	/** Preview on the map. Deliberately sends nothing. */
	const previewColor = useCallback(
		(objectKey, color, opacity) => setLiveStyling({ objectKey, color, opacity }),
		[]
	);

	/** Drop the preview — Escape, or a picker closed without a change. */
	const cancelColor = useCallback(() => setLiveStyling(null), []);

	/**
	 * @param {() => void} [onSettled] - called once the save has landed or failed.
	 *   The picker uses it to drop the draft it keeps showing in the meantime.
	 */
	const commitColor = useCallback(
		(objectKey, color, opacity, onSettled) => {
			setLiveStyling({ objectKey, color, opacity });
			save(
				{ [objectKey]: { style: { color, opacity } } },
				{
					onSettled: () => {
						// Guarded on the key: a second object edited while this
						// save is in flight owns the override by then, and
						// clearing it here would flash that object back to its
						// saved color.
						setLiveStyling(current =>
							current?.objectKey === objectKey ? null : current
						);
						onSettled?.();
					},
				}
			);
		},
		[save]
	);

	return { liveStyling, previewColor, commitColor, cancelColor, isSaving };
};

export default useObjectStyle;
